import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll } from 'framer-motion';
import { AuroraBackground } from '@/components/ui/aurora-background';
import { InfiniteSlider } from '@/components/ui/infinite-slider';
import { ProgressiveBlur } from '@/components/ui/progressive-blur';
import { Footer2 } from '@/components/ui/footer2';
import {
  Search,
  Mail,
  Brain,
  ChevronRight,
  Menu,
  X,
  MapPin,
  Youtube,
  Linkedin,
  Instagram,
  Twitter,
  Facebook,
  Zap,
  Download,
  Shield,
  Globe,
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
  Star,
} from 'lucide-react';

// ═══════════════════════════════════════════════
// FLOATING NAVBAR
// ═══════════════════════════════════════════════

function FloatingNavbar() {
  const [menuState, setMenuState] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setScrolled(latest > 0.01);
    });
    return unsubscribe;
  }, [scrollYProgress]);

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
  ];

  return (
    <header>
      <nav
        data-state={menuState ? 'active' : undefined}
        className="group fixed z-50 w-full pt-0"
      >
        <div
          className={`relative z-50 mx-auto max-w-7xl rounded-3xl px-6 transition-all duration-300 lg:px-12 ${
            scrolled ? 'bg-zinc-950/95 backdrop-blur-2xl shadow-lg shadow-black/20' : ''
          }`}
        >
          <motion.div
            className={`relative flex flex-wrap items-center justify-between gap-6 py-3 duration-200 lg:gap-0 lg:py-6 ${
              scrolled ? 'lg:py-4' : ''
            }`}
          >
            {/* Left Side */}
            <div className="flex w-full items-center justify-between gap-12 lg:w-auto">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2">
                <img
                  src="/logo-small.png"
                  alt="LeadGen AI"
                  className="h-7 w-auto"
                />
                <span className="text-lg font-bold text-white">
                  LeadGen<span className="text-blue-500"> AI</span>
                </span>
              </Link>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMenuState(!menuState)}
                className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu className="w-6 h-6 text-white duration-200 group-data-[state=active]:rotate-180 group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0" />
                <X className="absolute inset-0 m-auto w-6 h-6 -rotate-180 scale-0 opacity-0 duration-200 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100" />
              </button>

              {/* Desktop Nav Links */}
              <div className="hidden lg:block">
                <ul className="flex gap-8 text-sm">
                  {navItems.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-gray-400 hover:text-white block duration-150"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/faq"
                      className="text-gray-400 hover:text-white block duration-150"
                    >
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side (Desktop + Mobile Dropdown) */}
            <div className="bg-zinc-950 group-data-[state=active]:block lg:group-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-center space-y-6 rounded-2xl border border-white/10 p-6 shadow-2xl shadow-black/50 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none lg:justify-end relative z-50">
              {/* Mobile Nav */}
              <div className="lg:hidden">
                <ul className="space-y-5 text-base text-center">
                  {navItems.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        onClick={() => setMenuState(false)}
                        className="text-gray-400 hover:text-white block duration-150"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/faq"
                      onClick={() => setMenuState(false)}
                      className="text-gray-400 hover:text-white block duration-150"
                    >
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Auth Buttons */}
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit lg:w-fit">
                <Link
                  to="/login"
                  className="inline-flex h-9 items-center justify-center rounded-md border border-white/20 px-4 text-sm font-medium text-white hover:bg-white/5 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex h-9 items-center justify-center rounded-md bg-blue-500 px-4 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </nav>
    </header>
  );
}

// ═══════════════════════════════════════════════
// FEATURES DATA
// ═══════════════════════════════════════════════

const features = [
  {
    icon: Search,
    title: 'AI-Powered Search',
    description:
      "Just describe your ideal client in plain English. Our AI understands context, intent, and finds exactly who you need.",
  },
  {
    icon: Mail,
    title: 'Real Contact Data',
    description:
      'Get verified emails, phone numbers, WhatsApp links, and social media profiles. Ready to reach out immediately.',
  },
  {
    icon: Brain,
    title: 'Smart AI Analysis',
    description:
      'Know why they need you, how to approach them, and get a prospect score — before you even make contact.',
  },
];

// ═══════════════════════════════════════════════
// STEPS DATA
// ═══════════════════════════════════════════════

const steps = [
  {
    icon: MessageSquare,
    title: 'Describe Your Ideal Client',
    description:
      "Type in plain English: 'Find dentists in Mumbai who need websites' — our AI handles the rest.",
  },
  {
    icon: Search,
    title: 'AI Searches Everywhere',
    description:
      'We search Google Maps, YouTube, LinkedIn, Instagram, and more — across 82 countries.',
  },
  {
    icon: TrendingUp,
    title: 'Get Leads & Close Deals',
    description:
      'Export emails, phones, AI analysis. Know exactly who needs your service and why.',
  },
];

// ═══════════════════════════════════════════════
// STATS DATA
// ═══════════════════════════════════════════════

const stats = [
  { value: '10,000+', label: 'Leads Generated' },
  { value: '500+', label: 'Freelancers Trust Us' },
  { value: '8', label: 'Platforms Searched' },
  { value: '82', label: 'Countries Covered' },
];

// ═══════════════════════════════════════════════
// PRICING DATA
// ═══════════════════════════════════════════════

const pricingPlans = [
  {
    name: 'Free Trial',
    price: 'Free',
    period: '2 days',
    features: ['1000 leads per day', 'CSV & Excel export', 'Email finder', 'AI analysis'],
    buttonText: 'Start Free Trial',
    buttonClass: 'bg-white/10 text-white hover:bg-white/20',
    popular: false,
  },
  {
    name: 'Standard',
    price: '₹999',
    period: '/month',
    features: ['500 leads per day', 'CSV & Excel export', 'Quick AI scoring', 'Priority support'],
    buttonText: 'Get Started',
    buttonClass: 'bg-white/10 text-white hover:bg-white/20',
    popular: false,
  },
  {
    name: 'Premium',
    price: '₹2,499',
    period: '/month',
    features: [
      '1000 leads per day',
      'CSV & Excel export',
      'Email finder included',
      'Deep AI analysis',
      'Priority support',
    ],
    buttonText: 'Get Premium',
    buttonClass: 'bg-blue-500 text-white hover:bg-blue-600',
    popular: true,
  },
];

// ═══════════════════════════════════════════════
// MAIN LANDING PAGE
// ═══════════════════════════════════════════════

function LandingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const warmUp = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const healthUrl = apiUrl.replace(/\/api\/?$/, '') + '/health';
        await fetch(healthUrl);
        console.log('Backend warm-up ping sent');
      } catch (error) {
        // Silently ignore errors
      }
    };
    warmUp();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden">
      {/* SEO */}
      <Helmet>
        <title>LeadGen AI — Find Your Next Client with AI-Powered Lead Generation</title>
        <meta
          name="description"
          content="AI-powered lead generation for freelancers. Find business leads with real emails, phones, and AI analysis. Search Google Places, YouTube, LinkedIn and more."
        />
      </Helmet>

      {/* Floating Navbar */}
      <FloatingNavbar />

      {/* ═══════════════════════════════════════════
          HERO SECTION
          ═══════════════════════════════════════════ */}
      <AuroraBackground className="min-h-screen pt-20 md:pt-28">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: 'easeInOut' }}
          className="relative z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6 max-w-5xl mx-auto"
        >
          {/* Pill Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-gray-300">
            <Zap className="h-4 w-4 text-blue-400" />
            <span>AI-Powered Lead Generation</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
            Find Your Next Client
            <br className="hidden sm:block" />
            <span className="text-gradient">Powered by AI</span>
          </h1>

          {/* Subheading */}
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-gray-400 leading-relaxed">
            Tell us who you need. We find them, analyze them, and hand you their
            contact details — emails, phones, social media.
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/signup"
              className="group inline-flex h-12 items-center gap-2 rounded-full bg-blue-500 px-6 text-base font-semibold text-white hover:bg-blue-600 transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
            >
              <span>Start Free Trial</span>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 px-6 text-base font-medium text-white hover:bg-white/5 transition-all duration-200"
            >
              <span>See Pricing</span>
            </Link>
          </div>

          {/* Trust Text */}
          <p className="mt-6 text-sm text-gray-500">
            Free trial • No credit card required • 1000 leads/day
          </p>
        </motion.div>
      </AuroraBackground>

      {/* ═══════════════════════════════════════════
          PLATFORM SLIDER
          ═══════════════════════════════════════════ */}
      <section className="relative bg-zinc-950 py-12 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col items-center md:flex-row">
            {/* Left Label */}
            <div className="md:max-w-44 md:border-r md:border-white/10 md:pr-6 mb-4 md:mb-0">
              <p className="text-sm text-gray-500 text-center md:text-right">
                We find leads from
              </p>
            </div>

            {/* Slider */}
            <div className="relative py-4 md:w-[calc(100%-11rem)]">
              <InfiniteSlider duration={40} durationOnHover={80} gap={80}>
                <div className="flex items-center gap-2 text-gray-500">
                  <MapPin className="h-5 w-5" />
                  <span className="text-sm font-medium">Google Maps</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Youtube className="h-5 w-5" />
                  <span className="text-sm font-medium">YouTube</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Linkedin className="h-5 w-5" />
                  <span className="text-sm font-medium">LinkedIn</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Instagram className="h-5 w-5" />
                  <span className="text-sm font-medium">Instagram</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Facebook className="h-5 w-5" />
                  <span className="text-sm font-medium">Facebook</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Twitter className="h-5 w-5" />
                  <span className="text-sm font-medium">Twitter / X</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Globe className="h-5 w-5" />
                  <span className="text-sm font-medium">82 Countries</span>
                </div>
              </InfiniteSlider>

              {/* Edge Fading */}
              <ProgressiveBlur
                className="pointer-events-none absolute left-0 top-0 h-full w-20"
                direction="left"
                blurIntensity={1}
              />
              <ProgressiveBlur
                className="pointer-events-none absolute right-0 top-0 h-full w-20"
                direction="right"
                blurIntensity={1}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FEATURES
          ═══════════════════════════════════════════ */}
      <section id="features" className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">
              Everything You Need to <span className="text-gradient">Find Clients</span>
            </h2>
            <p className="mt-4 text-gray-400 text-lg max-w-2xl mx-auto">
              Stop cold searching. Let AI do the heavy lifting.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/50 p-8 hover:border-white/20 transition-all duration-300"
              >
                <div className="mb-6 inline-flex items-center justify-center rounded-xl bg-blue-500/10 p-3">
                  <feature.icon className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          HOW IT WORKS
          ═══════════════════════════════════════════ */}
      <section id="how-it-works" className="py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Three Steps. <span className="text-gradient">Real Leads.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative text-center"
              >
                {/* Big Step Number */}
                <div className="text-8xl font-black text-blue-500/10 absolute -top-4 left-1/2 -translate-x-1/2 select-none">
                  0{index + 1}
                </div>
                {/* Icon */}
                <div className="relative z-10 mb-6 inline-flex items-center justify-center rounded-full bg-blue-500/10 p-4 ring-1 ring-blue-500/20">
                  <step.icon className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          STATS BAR
          ═══════════════════════════════════════════ */}
      <section className="py-16 bg-zinc-950 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl md:text-4xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PRICING PREVIEW
          ═══════════════════════════════════════════ */}
      <section id="pricing" className="py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-gray-400 text-center mb-12 text-lg">
            Start free. Upgrade when you're ready.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 relative ${
                  plan.popular
                    ? 'border-2 border-blue-500 bg-zinc-900/50'
                    : 'border border-white/10 bg-zinc-900/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white">
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">
                    {plan.price}
                  </span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-3 text-sm text-gray-300"
                    >
                      <svg
                        className="h-4 w-4 text-blue-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`mt-8 block w-full text-center rounded-xl py-3 text-sm font-semibold transition-colors ${plan.buttonClass}`}
                >
                  {plan.buttonText}
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center mt-8">
            <Link
              to="/pricing"
              className="text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              Compare all plans →
            </Link>
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          BOTTOM CTA
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent p-12 md:p-16">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Find Your Next Client?
            </h2>
            <p className="mt-4 text-lg text-gray-400">
              Start your free trial today. No credit card required.
            </p>
            <Link
              to="/signup"
              className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-blue-500 px-8 text-base font-semibold text-white hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25"
            >
              <span>Get Started Free</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════ */}
      <Footer2 />
    </div>
  );
}

export default LandingPage;
